import { Box, Skeleton, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import {
  useIsMobile,
  ImageBox,
  IconButton,
} from '@tix-ticketing/ui-components';
import { CrossIcon } from '@tix-ticketing/ui-icons/outline';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { deleteApiOrdersByEncryptedOrderIdEventByEventId } from 'api/generated';
import { DeleteConfirmationDialog } from 'components/common/dialogs/delete-confirmation-dialog';
import { DiscountedPrice } from 'components/common/price/discounted-price';
import { useAppState } from 'lib/app-state';
import { toastError } from 'lib/helpers/error-toast-helpers';
import { useServerTimezone } from 'lib/hooks/use-server-timezone';

interface Props {
  eventId: number;
  eventName: string;
  eventSubtitle: string;
  eventStartsAt: string;
  eventImageUrl: string;
  loading?: boolean;
  eventTotal?: number;
  /** Pre-discount total. Only shown (struck through) when it differs from `eventTotal`. */
  originalEventTotal?: number;
  showRemoveAction?: boolean;
  /** Takes over the remove action: the event is left to the caller to remove,
   *  and neither the confirmation dialog nor the delete call happen here. */
  onRemove?: () => void;
  /** Hall name shown below the date (subscriptions). */
  hallName?: string;
  /** Square image size in px. Defaults to 80. */
  imageSize?: number;
  /**
   * Marks the event as cancelled: the date/time renders with a strike-through.
   * Dormant for now — the receipt API does not expose a cancellation flag, so
   * callers pass `false` until the backend provides one.
   */
  isCancelled?: boolean;
}

export const EventCardBody = ({
  eventId,
  eventName,
  eventSubtitle,
  eventStartsAt,
  eventImageUrl,
  loading,
  eventTotal,
  originalEventTotal,
  showRemoveAction,
  onRemove,
  hallName,
  imageSize = 80,
  isCancelled = false,
}: Props) => {
  const intl = useIntl();
  const isMobile = useIsMobile();
  const appState = useAppState();
  const { formatDate, formatTime } = useServerTimezone();

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const removeEventMutation = useMutation({
    mutationFn: () =>
      deleteApiOrdersByEncryptedOrderIdEventByEventId({
        path: {
          encryptedOrderId: appState.eventData?.order?.encryptedId ?? '0',
          eventId: eventId,
        },
      }),
    onSuccess: (data: any) => {
      if (data.status === 204) {
        appState.setEventData({
          event: undefined,
          order: undefined,
        });
      } else if (data.status !== 200) {
        toastError(data.response.data);
      } else {
        appState.setEventData({
          event: appState.eventData.event,
          order: data.data,
        });
      }
    },
  });

  if (!eventName || loading)
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box>
          <Skeleton variant="formItem" width={imageSize} height={imageSize} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <Skeleton variant="text" width={80} height={28} />
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );

  const image = (
    <ImageBox
      imageUrl={eventImageUrl}
      // Decorative: the event name sits beside it.
      altText=""
      variant="thumbnail"
      sx={{
        width: imageSize,
        height: imageSize,
        flexShrink: 0,
        bgcolor: 'monochrome.main',
      }}
    />
  );

  const title = (
    <Typography variant="h6" component="h2">
      {eventName}
    </Typography>
  );

  const subtitle = eventSubtitle ? (
    <Typography variant="body3" sx={{ color: 'text.secondary' }}>
      {eventSubtitle}
    </Typography>
  ) : null;

  const dateTime = !eventStartsAt ? null : (
    <Typography
      variant={isMobile ? 'body3' : 'body2'}
      sx={[
        { color: 'text.primary', display: 'flex', alignItems: 'center' },
        isCancelled && { textDecoration: 'line-through' },
      ]}
    >
      {formatDate(eventStartsAt, {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
      })}
      <Box
        component="span"
        sx={{
          width: 4,
          height: 4,
          mx: 1,
          borderRadius: '50%',
          bgcolor: 'text.secondary',
          flexShrink: 0,
        }}
      />
      {formatTime(eventStartsAt, {
        timeStyle: 'short',
      })}
    </Typography>
  );

  const hall = hallName ? (
    <Typography variant="body3" sx={{ color: 'text.secondary' }}>
      {hallName}
    </Typography>
  ) : null;

  const price =
    eventTotal !== undefined ? (
      <DiscountedPrice
        price={eventTotal}
        originalPrice={originalEventTotal}
        variant="body1Bold"
      />
    ) : null;

  const removeAction = showRemoveAction ? (
    <>
      <IconButton
        size="small"
        color="monochrome"
        onClick={() => (onRemove ? onRemove() : setIsDeleteDialogOpen(true))}
        label={intl.formatMessage({
          id: 'common.remove',
          defaultMessage: 'Remove',
        })}
        sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
      >
        <CrossIcon />
      </IconButton>
      {!onRemove && (
        <DeleteConfirmationDialog
          open={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDelete={() => removeEventMutation.mutate()}
          loading={removeEventMutation.isPending}
        />
      )}
    </>
  ) : null;

  // Cart layout (a per-event price is shown): a single inline row on desktop,
  // reflowing to two rows on mobile — image + title on top, date + price below.
  if (price) {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: { xs: 2, md: 3 },
            width: '100%',
            alignItems: { xs: 'flex-start', md: 'center' },
          }}
        >
          {image}
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            {title}
            {subtitle}
            <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 0.5 }}>
              {dateTime}
              {hall}
            </Box>
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>{price}</Box>
          {removeAction}
        </Box>
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Box>
            {dateTime}
            {hall}
          </Box>
          {price}
        </Box>
      </Box>
    );
  }

  // Default layout (subscription, receipt without a per-event price): image
  // beside a single text column. Top-aligned on mobile, centred on desktop.
  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 2, md: 3 },
        width: '100%',
        alignItems: { xs: 'flex-start', md: 'center' },
      }}
    >
      {image}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        {title}
        {subtitle}
        <Box sx={{ mt: 0.5 }}>{dateTime}</Box>
        {hall}
      </Box>
      {removeAction}
    </Box>
  );
};
